// Shared helpers for ordering magazine issue names ("MonYY", e.g. "Jul25")
// and filtering leaflet deliveries client-side.

const MONTH_MAP = {
  Jan: 1, Feb: 2, Mar: 3, Apr: 4, May: 5, Jun: 6,
  Jul: 7, Aug: 8, Sep: 9, Oct: 10, Nov: 11, Dec: 12,
};

// Parse an issue name like "Jul25" into a sortable number (YYYYMM). Returns
// the trimmed original string if it does not match the "MonYY" pattern.
const parseIssue = issue => {
  const issueStr = String(issue).trim();
  const match = issueStr.match(/^([A-Za-z]{3})(\d{2})$/);
  if (match) {
    const month = MONTH_MAP[match[1]];
    const year = parseInt(match[2], 10);
    if (!month) return issueStr;
    const fullYear = year <= 50 ? 2000 + year : 1900 + year;
    return fullYear * 100 + month;
  }
  return issueStr;
};

// Order two issue names chronologically. Non-parseable names fall back to a
// locale string comparison. Returns a negative, zero, or positive number.
export const compareIssues = (issueA, issueB) => {
  const valA = parseIssue(issueA);
  const valB = parseIssue(issueB);
  if (typeof valA === 'number' && typeof valB === 'number') {
    return valA - valB;
  }
  return String(valA).localeCompare(String(valB));
};

// True if a delivery's run (startIssue..finishIssue, inclusive) covers the
// given issue. An empty issueName means "no issue filter" and returns true.
export const isDeliveryLiveInIssue = (delivery, issueName) => {
  if (!issueName) return true;
  if (!delivery || !delivery.startIssue || !delivery.finishIssue) return false;
  return (
    compareIssues(delivery.startIssue, issueName) <= 0 &&
    compareIssues(issueName, delivery.finishIssue) <= 0
  );
};

// Apply all leaflet-delivery filters, AND-combined. Any empty filter value is
// ignored. filters = { search, issue, publication, startIssue, finishIssue }.
export const filterLeafletDeliveries = (deliveries, filters = {}) => {
  if (!Array.isArray(deliveries)) return [];
  const search = (filters.search || '').trim().toLowerCase();

  return deliveries.filter(delivery => {
    if (!delivery) return false;
    if (search && !delivery.customer?.name?.toLowerCase().includes(search)) {
      return false;
    }
    if (filters.publication && delivery.magazine?._id !== filters.publication) {
      return false;
    }
    if (filters.startIssue && delivery.startIssue !== filters.startIssue) {
      return false;
    }
    if (filters.finishIssue && delivery.finishIssue !== filters.finishIssue) {
      return false;
    }
    if (!isDeliveryLiveInIssue(delivery, filters.issue)) {
      return false;
    }
    return true;
  });
};

// Sorted, de-duped list of every issue name across all magazine schedules.
// Populates the "Issue" dropdown so mid-run issues are always selectable.
export const getScheduleIssueOptions = magazines => {
  if (!Array.isArray(magazines)) return [];
  const names = new Set();
  magazines.forEach(magazine => {
    const issues = magazine?.schedule?.issues;
    if (Array.isArray(issues)) {
      issues.forEach(issue => {
        if (issue?.name) names.add(issue.name);
      });
    }
  });
  return Array.from(names).sort(compareIssues);
};

// Sorted, de-duped list of the values a given field takes across deliveries.
// Used for the Start / Finish dropdowns (field = 'startIssue' | 'finishIssue').
export const getDeliveryIssueValues = (deliveries, field) => {
  if (!Array.isArray(deliveries)) return [];
  const values = new Set();
  deliveries.forEach(delivery => {
    const value = delivery?.[field];
    if (value) values.add(value);
  });
  return Array.from(values).sort(compareIssues);
};

// Map of issue name -> close date (Date) across all magazine schedules. When a
// name appears in several schedules the latest close date wins, so an issue is
// only treated as "closed" once every publication's deadline for it has passed.
export const buildIssueCloseMap = magazines => {
  const map = new Map();
  if (!Array.isArray(magazines)) return map;
  magazines.forEach(magazine => {
    const issues = magazine?.schedule?.issues;
    if (!Array.isArray(issues)) return;
    issues.forEach(issue => {
      if (!issue?.name || !issue?.closeDate) return;
      const date = new Date(issue.closeDate);
      if (Number.isNaN(date.getTime())) return;
      const existing = map.get(issue.name);
      if (!existing || date > existing) map.set(issue.name, date);
    });
  });
  return map;
};

// The "current" issue: the earliest issue (chronologically) whose deadline is
// still today or in the future. Dates are compared at day granularity to match
// isDatePast. Returns null when every issue is closed. closeMap comes from
// buildIssueCloseMap.
export const getCurrentIssueName = (closeMap, now = new Date()) => {
  const today = new Date(now);
  today.setHours(0, 0, 0, 0);

  const openIssues = Array.from(closeMap.entries())
    .filter(([, date]) => {
      const day = new Date(date);
      day.setHours(0, 0, 0, 0);
      return day >= today;
    })
    .map(([name]) => name)
    .sort(compareIssues);

  return openIssues.length > 0 ? openIssues[0] : null;
};
